import { c as _c } from "react/compiler-runtime";
import type { Icon } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import { type ReactNode, type Ref, useId } from 'react';
type CountTone = 'neutral' | 'warning' | 'error';
type SectionCount = {
  count: number;
  countTone?: CountTone;
  countDescription?: string;
  isCountPending?: boolean;
};
const countToneClassName: Record<CountTone, string> = {
  neutral: 'text-tertiary',
  warning: 'text-warning',
  error: 'text-error'
};
type OnboardingSectionProps = {
  title: string;
  icon?: Icon;
  count?: SectionCount;
  action?: ReactNode;
  children: ReactNode;
  ref?: Ref<HTMLElement>;
};

/** One section of an onboarding: a title row, with its count and action, over the section's own cards. */
const OnboardingSection = t0 => {
  const $ = _c(19);
  const {
    title,
    icon: TitleIcon,
    count,
    action,
    children,
    ref
  } = t0;
  const headingId = useId();
  let t1;
  if ($[0] !== TitleIcon) {
    t1 = TitleIcon && <TitleIcon className="mr-0.5" />;
    $[0] = TitleIcon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== count) {
    t2 = count && <>{" "}<span className={cx("text-body-2 tabular-nums", count.isCountPending ? "invisible" : countToneClassName[count.countTone ?? "neutral"])}><span aria-hidden={Boolean(count.countDescription) || count.isCountPending}>({count.count})</span>{count.countDescription && !count.isCountPending && <span className="sr-only">{count.countDescription}</span>}</span></>;
    $[2] = count;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== headingId || $[5] !== t1 || $[6] !== t2 || $[7] !== title) {
    t3 = <h4 id={headingId} className="flex items-center gap-1.5 text-label-2 text-primary">{t1}{title}{t2}</h4>;
    $[4] = headingId;
    $[5] = t1;
    $[6] = t2;
    $[7] = title;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== action || $[10] !== t3) {
    t4 = <div className="flex min-h-8 flex-wrap items-center justify-between gap-x-3 gap-y-1">{t3}{action}</div>;
    $[9] = action;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== children) {
    t5 = <div className="@container relative [&>*:first-child]:p-0">{children}</div>;
    $[12] = children;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== headingId || $[15] !== ref || $[16] !== t4 || $[17] !== t5) {
    t6 = <section ref={ref} aria-labelledby={headingId} tabIndex={-1} className="flex min-w-0 flex-col gap-2 outline-none">{t4}{t5}</section>;
    $[14] = headingId;
    $[15] = ref;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
export type { SectionCount };
export default OnboardingSection;